import React, { useCallback, useEffect, useState } from "react";
import { FormHelperText, Typography, useTheme } from "@mui/material";
import DatePicker from "@components/DatePicker/DatePicker";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import type { MovementError } from "@features/vessel-call/vessel-call-status/MovementServices/form/utils.ts";
import type { Movement } from "@services/movement-services/types";
import { useQueryClient } from "@tanstack/react-query";
import { toMinute } from "@utils/date-time";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import z from "zod";
import { useCompleteMovement } from "../hooks/useCompleteMovement";
import {
  getUniqueMovementUploadFiles,
  MAX_MOVEMENT_ATTACHMENTS,
  useMovementAttachmentUploads,
  type MovementAttachment,
} from "../hooks/useMovementAttachmentUploads";
import { FieldError } from "./ApproveMovementSevice";

const asDateOrNull = (v: unknown) => (v == null || v === "" ? null : (v as Date));

const isNotPastDateTime = (value: Date) => {
  const valueMinute = toMinute(value);
  const nowMinute = toMinute(new Date());

  return !!valueMinute && !!nowMinute && valueMinute.getTime() >= nowMinute.getTime();
};

// This form does not have a store defined, so all fields will be managed via local state or props
// => use regular input components and manage their state locally or via props
interface NonDisputeMovementServiceProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  currentMovements: Movement[];
}
interface OutletContextType {
  vesselCallDetails: { id: string; vesselId: string };
}
const serviceVesselCall = "bff-vessel-call";

const NonDisputeMovementService: React.FC<NonDisputeMovementServiceProps> = ({
  isOpen,
  onClose,
  currentMovements,
}) => {
  const { vesselCallDetails } = useOutletContext<OutletContextType>();
  const vesselId = vesselCallDetails?.vesselId;
  const vesselCallId = vesselCallDetails?.id;

  const currentMovement = currentMovements?.[0];

  const isBerthToSea = currentMovement?.movementType === "BERTH_TO_SEA";
  const isBerthToBerth = currentMovement?.movementType === "BERTH_TO_BERTH";
  const isBerthToAnchorage = currentMovement?.movementType === "BERTH_TO_ANCHORAGE";

  const { t } = useTranslation();
  const [rtd, setRtd] = useState<Date | null>(null);
  const theme = useTheme();
  const [additionalErrors, setAdditionalErrors] = useState<{
    rtd?: string;
    comments?: string;
    externalFiles?: string;
    unberthPlan?: string;
  }>({});
  const { notify } = useNotification();
  const TEXTAREA_MAX_CHARS = 500;
  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };
  const sxProps = {
    "& .MuiInputBase-root": {
      borderRadius: "8px",
      borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
      padding: "12px",
      fontSize: theme.typography.body1.fontSize,
      color: theme.palette.text.primary,
    },
    "& textarea": {
      padding: 0,
    },
  };
  const inputSlotProps = {
    root: {
      className: `rounded-lg text-sm px-4 py-2 border`,
      style: {
        borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
        color: theme.palette.text.primary,
      },
    },
    htmlInput: {
      maxLength: TEXTAREA_MAX_CHARS,
    },
  };
  const styles = getSideDrawerStyles(theme);
  const [comments, setComments] = useState("");
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const allowedSideDrawerExtensions = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile.format.map(
    (ext) => (ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`),
  );
  const externalAttachmentsFile = useUploadStore(
    (state) => state.filesByKey.nonDisputeExternalAttachmentsFile ?? EMPTY_UPLOAD_ENTRIES,
  );
  const unberthPlanFile = useUploadStore(
    (state) => state.filesByKey.unberthPlanFile ?? EMPTY_UPLOAD_ENTRIES,
  );
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const setExternalAttachmentsFile = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) clearFilesForKey("nonDisputeExternalAttachmentsFile");
      else setFilesForKey("nonDisputeExternalAttachmentsFile", files);
    },
    [clearFilesForKey, setFilesForKey],
  );
  const setUnberthPlanFile = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) clearFilesForKey("unberthPlanFile");
      else setFilesForKey("unberthPlanFile", files);
    },
    [clearFilesForKey, setFilesForKey],
  );
  const unberthPlanUploads = useMovementAttachmentUploads(
    "NonDisputeMovementService",
    "unberthPlanFile",
  );
  useEffect(() => {
    if (!isOpen) return;
    const existingUnberthPlan = currentMovement?.unberthPlan?.[0];
    setUnberthPlanFile(
      existingUnberthPlan
        ? [
            {
              name: existingUnberthPlan.originalFileName || "Unberth Plan",
              url: existingUnberthPlan.url,
              type: existingUnberthPlan.type,
            },
          ]
        : [],
    );
  }, [currentMovement?.id, currentMovement?.unberthPlan, isOpen, setUnberthPlanFile]);
  const attachmentUploads = useMovementAttachmentUploads(
    "NonDisputeMovementService",
    "nonDisputeExternalAttachmentsFile",
  );
  const handleFileUpload = (newFiles: File[]) => {
    const files = getUniqueMovementUploadFiles(newFiles, MAX_MOVEMENT_ATTACHMENTS);
    setAdditionalErrors((prev) => ({ ...prev, externalFiles: undefined }));
    setFilesForKey("nonDisputeExternalAttachmentsFile", files, { mode: "merge" });
    attachmentUploads.mutate({ files, documentType: "EXTERNAL_NOTES" });
  };
  const queryClient = useQueryClient();

  const rtdLabel =
    isBerthToSea || isBerthToBerth || isBerthToAnchorage
      ? t("vessel-call.movement-services.preview.fields.rtd-readiness")
      : t("vessel-call.movement-services.preview.fields.rtd");

  const rtdSchema = z.object({
    rtd: z
      .date({
        required_error: t("form-validation.required", { label: rtdLabel }),
        invalid_type_error: t("form-validation.required", { label: rtdLabel }),
      })
      .refine(isNotPastDateTime, {
        message: t("form-validation.must-not-be-in-past", { label: rtdLabel }),
      }),
  });
  const commentsOnlySchema = z.object({
    comments: z
      .string()
      .max(
        TEXTAREA_MAX_CHARS,
        t("form-validation.max-length", {
          label: t("vessel-call.movement-services.close-form.fields.notes-to-agent"),
          max: TEXTAREA_MAX_CHARS,
        }),
      )
      .optional(),
  });

  const resetFormValues = () => {
    setRtd(null);
    setComments("");
    setExternalAttachmentsFile([]);
    setUnberthPlanFile([]);
    attachmentUploads.reset();
    unberthPlanUploads.reset();
    setAdditionalErrors({});
  };

  const handleSuccess = async () => {
    setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
      });
      queryClient.invalidateQueries({
        queryKey: ["movement-service-preview"],
      });
    }, 2000);
    onClose("nonDisputeDrawer");
    resetFormValues();
    notify({
      title: t("vessel-call.movement-services.movement-non-dispute-approved"),
      type: "success",
    });
  };

  const handleCloseDrawer = () => {
    onClose("nonDisputeDrawer");
    resetFormValues();
  };

  const handleError = (error: MovementError) => {
    queryClient.invalidateQueries({
      queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
    });
    notify({
      title: t("common.error"),
      description:
        error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
      type: "error",
    });
    if (error.status === 409) {
      handleCloseDrawer();
    }
  };

  const { completeMovement } = useCompleteMovement({
    handleSuccess,
    handleError,
  });

  const handleSubmit = async () => {
    if (attachmentUploads.isPending || unberthPlanUploads.isPending) {
      notify({ title: t("common.notification.please-wait-for-upload"), type: "warning" });
      return;
    }
    const incompleteUpload = [
      { files: externalAttachmentsFile, error: attachmentUploads.error },
      { files: unberthPlanFile, error: unberthPlanUploads.error },
    ].find(({ files }) => files.some((file) => file instanceof File || !file.url));
    if (incompleteUpload) {
      notify({
        title: t("common.error"),
        description: incompleteUpload.error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
      return;
    }
    const payload: {
      status: "NON_DISPUTED";
      rtd?: string;
      externalNotes?: string;
      externalAttachments?: MovementAttachment[];
      unberthPlan?: [MovementAttachment];
    } = {
      status: "NON_DISPUTED",
    };
    const rtdResult = rtdSchema.safeParse({ rtd });
    const commentsResult = commentsOnlySchema.safeParse({ comments });

    setAdditionalErrors((prev) => ({
      ...prev,
      rtd: rtdResult.success ? undefined : rtdResult.error.flatten().fieldErrors.rtd?.[0],
      comments: commentsResult.success
        ? undefined
        : commentsResult.error.flatten().fieldErrors.comments?.[0],
    }));

    if (!rtdResult.success || !commentsResult.success) {
      return;
    }
    payload.rtd = (rtd as Date).toISOString();
    if (comments.trim().length > 0) {
      payload.externalNotes = comments.trim();
    }
    if (externalAttachmentsFile.length) {
      payload.externalAttachments = externalAttachmentsFile.flatMap((file) =>
        file instanceof File || !file.url
          ? []
          : [
              {
                originalFileName: file.name || "attachment",
                url: file.url,
                type: "EXTERNAL_ATTACHMENT",
              },
            ],
      );
    }
    if (unberthPlanFile[0] && !(unberthPlanFile[0] instanceof File) && unberthPlanFile[0].url) {
      payload.unberthPlan = [
        {
          originalFileName: unberthPlanFile[0].name || "attachment",
          url: unberthPlanFile[0].url,
          type: "UNBERTH_PLAN",
        },
      ];
    }

    await completeMovement({
      id: currentMovement.id || "",
      service: serviceVesselCall,
      payload,
    });
  };

  return (
    <div data-testid="nonDispute-movement-service-form" className="max-h-screen overflow-y-auto">
      <SideDrawer
        isOpen={isOpen}
        title={t(`vessel-call.movement-services.approve-title`)}
        onClose={handleCloseDrawer}
        submitLabel={t(`vessel-call.movement-services.approve-button`)}
        onSubmit={handleSubmit}
        keepMounted={false}
      >
        <div className="w-[400px] max-w-full px-6">
          <Typography
            variant="body1"
            data-testid="movement-service-form-title"
            sx={{ color: "text.primary", pb: 4 }}
          >
            {t(`vessel-call.movement-services.approve-subtitle`)}
          </Typography>
          <div className="py-3 flex flex-col gap-2">
            <CustomFormLabel>{rtdLabel}</CustomFormLabel>
            <DatePicker
              quickCapture
              value={asDateOrNull(rtd)}
              onChange={(d) => {
                setRtd(d);
                const result = rtdSchema.safeParse({ rtd: d as Date });
                setAdditionalErrors((p) => ({
                  ...p,
                  rtd: result.success ? undefined : result.error.flatten().fieldErrors.rtd?.[0],
                }));
              }}
              testId={`rtd-date-input`}
              timePicker={true}
              dateConstraint="not-past"
              combinedInput={true}
              hasError={!!additionalErrors.rtd}
            />
            {additionalErrors.rtd && (
              <FieldError testId="rtd-error">{additionalErrors.rtd}</FieldError>
            )}
          </div>
          <div className="col-span-full mb-0 flex flex-col gap-2 mt-6">
            <CustomFormLabel>
              {t("vessel-call.movement-services.notes-to-the-agent")}
            </CustomFormLabel>
            <Input
              placeholder={t("vessel-call.movement-services.notes-to-the-agent")}
              multiline
              minRows={3}
              fullWidth
              variant="outlined"
              value={comments}
              onKeyDown={(e) => {
                if (!isPrintableKey(e)) return;
                if (comments.length >= TEXTAREA_MAX_CHARS) {
                  setAdditionalErrors((prev) => ({
                    ...prev,
                    comments: t("form-validation.max-length", {
                      label: t("vessel-call.movement-services.comments"),
                      max: TEXTAREA_MAX_CHARS,
                    }),
                  }));
                }
              }}
              onBlur={() => {
                setAdditionalErrors((prev) => ({
                  ...prev,
                  comments: undefined,
                }));
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                const value = e.target.value;
                setComments(value);
                const result = commentsOnlySchema.safeParse({
                  comments: value,
                });
                setAdditionalErrors((prev) => ({
                  ...prev,
                  comments: result.success
                    ? undefined
                    : result.error.flatten().fieldErrors.comments?.[0],
                }));
              }}
              slotProps={inputSlotProps}
              sx={sxProps}
              testId={"additional-notes-input"}
            />
            {additionalErrors.comments && (
              <p style={styles.errorText} className="text-sm mt-1">
                {additionalErrors.comments}
              </p>
            )}
            <FormHelperText sx={charCounterSx}>
              {comments.length}/{TEXTAREA_MAX_CHARS}
            </FormHelperText>
          </div>
          <div className="col-span-full mt-6">
            <div
              className={additionalErrors.unberthPlan ? "border border-red-500 rounded-md p-1" : ""}
            >
              <DocumentUpload
                label={t("vessel-management.vessel-status-page.unberthPlan")}
                testId="unberth-plan-upload"
                name="unberthPlanFile"
                isError={Boolean(additionalErrors.unberthPlan)}
                maxFiles={1}
                allowedExtensions={allowedSideDrawerExtensions}
                onUpload={(newFiles) => {
                  if (newFiles.length > 1) {
                    setAdditionalErrors((prev) => ({
                      ...prev,
                      unberthPlan: t("upload.one-document"),
                    }));
                    return;
                  }
                  const file = newFiles[0];
                  if (!file) return;
                  setAdditionalErrors((prev) => ({ ...prev, unberthPlan: undefined }));
                  setUnberthPlanFile([file]);
                  unberthPlanUploads.mutate({ files: [file], documentType: "UNBERTH_PLAN" });
                }}
                conditions={FILE_UPLOAD_PROFILES.sideDrawerApprovalFile}
                files={unberthPlanFile}
                disabled={unberthPlanUploads.isPending}
                setFiles={setUnberthPlanFile}
                uploadDescription={t("upload.dragOrDropDescription")}
              />
            </div>
            {additionalErrors.unberthPlan && (
              <p style={styles.errorText} className="text-sm mt-1">
                {additionalErrors.unberthPlan}
              </p>
            )}
            {unberthPlanUploads.error && (
              <p style={styles.errorText} className="text-sm mt-1">
                {unberthPlanUploads.error.message}
              </p>
            )}
            {unberthPlanFile[0] && !additionalErrors.unberthPlan && (
              <div className="flex-1 mt-4">
                <SingleFile
                  file={unberthPlanFile[0]}
                  fieldName="unberthPlanFile"
                  testId="unberth-plan-upload"
                  isLoading={unberthPlanUploads.isPending && unberthPlanFile[0] instanceof File}
                  setFiles={() => {
                    setAdditionalErrors((prev) => ({ ...prev, unberthPlan: undefined }));
                    setUnberthPlanFile([]);
                    if (!unberthPlanUploads.isPending) unberthPlanUploads.reset();
                  }}
                />
              </div>
            )}
          </div>
          <div className="col-span-full mt-6">
            <input type="hidden" name="externalAttachmentsUrl" />
            <div
              className={
                additionalErrors.externalFiles ? "border border-red-500 rounded-md p-1" : ""
              }
            >
              <DocumentUpload
                label={t("vessel-call.movement-services.close-form.fields.attachments")}
                testId="external-file-upload"
                name="nonDisputeExternalAttachmentsFile"
                isError={Boolean(additionalErrors.externalFiles)}
                maxFiles={MAX_MOVEMENT_ATTACHMENTS}
                disabled={attachmentUploads.isPending}
                allowedExtensions={allowedSideDrawerExtensions}
                onUpload={handleFileUpload}
                conditions={FILE_UPLOAD_PROFILES.sideDrawerApprovalFile}
                files={externalAttachmentsFile}
                setFiles={setExternalAttachmentsFile}
                uploadDescription={t("upload.dragOrDropDescription")}
              />
            </div>
            {additionalErrors.externalFiles && (
              <p style={styles.errorText} className="text-sm mt-1">
                {additionalErrors.externalFiles}
              </p>
            )}
            {attachmentUploads.error && (
              <p style={styles.errorText} className="text-sm mt-1">
                {attachmentUploads.error.message}
              </p>
            )}
            {!additionalErrors.externalFiles &&
              externalAttachmentsFile.map((file, index) => (
                <div key={`${file.name}-${index}`} className="flex-1 mt-4">
                  <SingleFile
                    file={file}
                    fieldName="nonDisputeExternalAttachmentsFile"
                    currentFileIndex={index}
                    testId="externalAttachment-upload"
                    isLoading={attachmentUploads.isPending && file instanceof File}
                    setFiles={() => {
                      setAdditionalErrors((prev) => ({ ...prev, externalFiles: undefined }));
                      if (!attachmentUploads.isPending) attachmentUploads.reset();
                    }}
                  />
                </div>
              ))}
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default NonDisputeMovementService;
